import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowRight, Users, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { ORGANS } from '../mockData';
import SEOHead from '../components/SEOHead';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const OrganDonation = () => {
  const [stats, setStats] = useState({
    registeredPledges: 0,
    countriesRepresented: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrganStats();
  }, []);

  const fetchOrganStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/organ`);
      setStats({
        registeredPledges: response.data.registeredPledges || 0,
        countriesRepresented: response.data.countriesRepresented || 0
      });
    } catch (error) {
      // Error handled silently - stats will remain at default values
    } finally {
      setLoading(false);
    }
  };

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://donorshouse.com';

  // Schema.org structured data for Organ Donation page
  const organDonationSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Organ Donation Information",
    "description": "Learn about organ donation and how one organ donor can save up to 8 lives. Find information about what organs can be donated, how to become a donor, and the impact of organ donation.",
    "url": `${siteUrl}/organ-donation`,
    "about": {
      "@type": "Thing",
      "name": "Organ Donation",
      "description": "The process of donating organs to save lives"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What organs can be donated?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Organs that can be donated include heart, lungs, liver, kidneys, pancreas, and intestines. Tissues such as corneas, skin, bone, and heart valves can also be donated. One organ donor can save up to 8 lives."
          }
        },
        {
          "@type": "Question",
          "name": "How do I become an organ donor?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "To become an organ donor, register your pledge on DonorsHouse, inform your family about your decision, and ensure your wishes are documented. The registration process is simple and takes just a few minutes."
          }
        },
        {
          "@type": "Question",
          "name": "How many lives can one organ donor save?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "One organ donor can save up to 8 lives through organ donation and enhance more than 75 others through tissue donation."
          }
        }
      ]
    }
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Become an Organ Donor",
    "description": "Step-by-step guide to registering as an organ donor",
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "Register Your Pledge",
        "text": "Sign up on DonorsHouse platform and select the organs you wish to donate. Choose between living or deceased donation options."
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "Inform Your Family",
        "text": "Share your decision with your loved ones. Their support is important in ensuring your wishes are honored."
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "Make an Impact",
        "text": "Your registration stays on file. When the time comes, your gift can save up to 8 lives and help many more."
      }
    ]
  };

  const breadcrumbs = [
    { name: 'Home', url: siteUrl },
    { name: 'Organ Donation', url: `${siteUrl}/organ-donation` }
  ];

  return (
    <>
      <SEOHead
        title="Organ Donation | Save Up to 8 Lives | Become an Organ Donor | DonorsHouse"
        description="Learn about organ donation and how one organ donor can save up to 8 lives. Find information about what organs can be donated, how to become a donor, and register your pledge to save lives."
        keywords="organ donation, become organ donor, organ transplant, save lives, organ donation registration, deceased donation, living donation, organ donor pledge"
        schema={[organDonationSchema, howToSchema]}
        breadcrumbs={breadcrumbs}
      />
      <div className="bg-white">
      <section className="relative overflow-hidden" aria-label="Organ donation hero">
        {/* Hero image background */}
        <div className="absolute inset-0 z-0">
          <img
            src="/organ-donation-hero.jpg"
            alt="Human silhouette showing organs connected to lives saved"
            className="w-full h-full object-cover object-center"
            width="1920"
            height="700"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-950/92 via-red-900/85 to-red-800/65" aria-hidden="true" />
        </div>
        {/* SVG wave */}
        <div className="absolute bottom-0 left-0 right-0 z-10" aria-hidden="true">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 50C96 40 192 20 288 15C384 10 480 20 576 27.5C672 35 768 40 864 37.5C960 35 1056 25 1152 20C1248 15 1344 15 1392 15L1440 15V60H0Z" fill="white"/>
          </svg>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-40 text-center">
          <div className="animate-fade-in-up max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
              <Heart className="h-4 w-4 text-red-300" aria-hidden="true" fill="currentColor" />
              <span className="text-red-100 text-sm font-medium tracking-wide uppercase">Give the Ultimate Gift</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white mb-4 leading-tight">
              Organ{' '}<span className="text-red-300">Donation</span>
            </h1>
            <p className="text-2xl sm:text-3xl font-bold text-red-200 mb-4">Saves Lives</p>
            <p className="text-lg mb-10 text-red-100 max-w-2xl mx-auto leading-relaxed">
              One organ donor can save up to <strong className="text-white">8 lives</strong> and enhance more than
              <strong className="text-white"> 75 others</strong>. Your decision is one of the most generous gifts you can give.
            </p>
            <Link to="/register?type=organ">
              <Button
                size="lg"
                className="bg-white text-red-950 hover:bg-red-50 font-bold text-lg px-10 py-6 btn-transition shadow-xl animate-pulse-glow"
                aria-label="Register your organ donation pledge"
              >
                Pledge to Donate
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-white" aria-label="Organ donation impact statistics">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: '17', label: 'People die daily waiting for an organ transplant', icon: Heart },
              { value: '100K+', label: 'People on the national transplant waiting list', icon: Users },
              { value: '8', label: 'Lives saved by a single organ donor', icon: Heart },
              { value: '75+', label: 'Lives enhanced through tissue donation', icon: Globe },
            ].map(({ value, label, icon: Icon }, i) => (
              <Card key={label} className={`text-center border-none shadow-lg card-hover animate-fade-in-up delay-${(i+1)*100}`}>
                <CardContent className="pt-8 pb-6">
                  <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-6 w-6 text-red-700" aria-hidden="true" fill="currentColor" />
                  </div>
                  <div className="text-4xl font-extrabold text-red-700 mb-2">{value}</div>
                  <p className="text-gray-700 font-medium text-sm leading-snug">{label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Can Be Donated */}
      <section className="py-20 lg:py-28 bg-gray-50" aria-label="What organs can be donated">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-4">
              What Can Be Donated?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              One donor can save and enhance many lives through organ and tissue donation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ORGANS.filter(o => o.livesToSave > 0).map((organ, idx) => (
              <Card key={organ.id} className={`border-none shadow-lg card-hover bg-white group animate-fade-in-up delay-${Math.min((idx+1)*100, 500)}`}>
                <CardHeader>
                  <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-red-50 rounded-2xl flex items-center justify-center mb-4 group-hover:from-red-200 transition-colors">
                    <Heart className="h-7 w-7 text-red-700" aria-hidden="true" fill="currentColor" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">{organ.name.replace(' (partial)', '')}</CardTitle>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">
                    {organ.id === 'kidney' && 'Two kidneys can save two lives'}
                    {organ.id === 'liver' && 'Can be split to save multiple lives'}
                    {organ.id === 'heart' && 'A single heart transplant can save one life'}
                    {organ.id === 'lungs' && 'Two lungs can save two lives'}
                    {organ.id === 'pancreas' && 'Can cure insulin-dependent diabetes'}
                    {organ.id === 'corneas' && 'Can restore sight to two people'}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="inline-flex items-center gap-1.5 bg-red-100 text-red-900 text-sm font-bold px-3 py-1.5 rounded-full">
                    <Heart className="h-4 w-4 text-red-700" aria-hidden="true" fill="currentColor" />
                    Saves {organ.livesToSave} {organ.livesToSave === 1 ? 'life' : 'lives'}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How to Become a Donor */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How to Become a Donor
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Simple steps to register your pledge and make a difference
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-700 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold">
                01
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Register Your Pledge</h3>
              <p className="text-gray-700 text-lg">
                Sign up on our platform and select the organs you wish to donate. Choose between living or deceased donation options.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-red-700 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold">
                02
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Inform Your Family</h3>
              <p className="text-gray-700 text-lg">
                Share your decision with your loved ones. Their support is important in ensuring your wishes are honored.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-red-700 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold">
                03
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Make an Impact</h3>
              <p className="text-gray-700 text-lg">
                Your registration stays on file. When the time comes, your gift can save up to 8 lives and help many more.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Summary */}
      <section className="py-12 bg-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold text-red-900 mb-2">
                {loading ? '0+' : `${stats.registeredPledges}+`}
              </div>
              <p className="text-gray-800 font-medium">Registered Pledges</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-900 mb-2">
                {loading ? '0' : stats.countriesRepresented}
              </div>
              <p className="text-gray-800 font-medium">Countries Represented</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-900 mb-2">100%</div>
              <p className="text-gray-800 font-medium">Secure & Confidential</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-900 mb-2">24/7</div>
              <p className="text-gray-800 font-medium">Support Available</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-24 relative overflow-hidden" aria-label="Register as organ donor">
        <div className="absolute inset-0 hero-gradient" aria-hidden="true" />
        <div className="absolute inset-0 opacity-5" aria-hidden="true"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-float mb-6" aria-hidden="true">
            <Heart className="h-16 w-16 text-red-300 mx-auto" fill="currentColor" />
          </div>
          <h2 className="text-3xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
            Ready to Make a Difference?
          </h2>
          <p className="text-xl text-red-100 mb-10 max-w-2xl mx-auto">
            Your decision today could give someone a second chance at life tomorrow.
          </p>
          <Link to="/register?type=organ">
            <Button
              size="lg"
              className="bg-white text-red-950 hover:bg-red-50 font-bold text-lg px-12 py-7 btn-transition shadow-2xl"
              aria-label="Register as an organ donor on DonorsHouse"
            >
              Register as an Organ Donor
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </Button>
          </Link>
        </div>
      </section>
      </div>
    </>
  );
};

export default OrganDonation;
